import type { JSX } from 'react'

import { useLayoutEffect, useRef, useState } from 'react'
import { HintsArrowButton } from './components/HintsArrowButton'
import { HintsScrollContext } from './HintsContainer.h'
import {
  HintsCarouselControlSC,
  HintsCarouselWrapperSC,
  HintsContainerSC,
  HintsScrollableRowSC,
} from './HintsContainer.styled'
import { useHintsScrollHandlers } from './hooks/useHintsScrollHandlers'

export interface IHintsContainerProps {
  children: React.ReactNode
}

export const HintsContainer = ({
  children,
}: IHintsContainerProps): JSX.Element => {
  const rowRef = useRef<HTMLDivElement>(null)
  const [rowScrollPosition, setRowScrollPosition] = useState(0)
  const [isNextControlVisible, setIsNextControlVisible] = useState(false)
  const [isPrevControlVisible, setIsPrevControlVisible] = useState(false)

  const { next, prev, handleScroll } = useHintsScrollHandlers({
    ref: rowRef,
    setRowScrollPosition,
  })

  const updateControlsVisibility = () => {
    if (!rowRef.current) return

    const { scrollLeft, scrollWidth, clientWidth } = rowRef.current
    const maxScrollLeft = scrollWidth - clientWidth

    setIsNextControlVisible(scrollLeft < maxScrollLeft - 1)
    setIsPrevControlVisible(scrollLeft > 1)
  }

  useLayoutEffect(() => {
    updateControlsVisibility()
  }, [rowScrollPosition])

  useLayoutEffect(() => {
    const container = rowRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      updateControlsVisibility()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  return (
    <HintsScrollContext.Provider value={rowRef}>
      <HintsContainerSC>
        <HintsCarouselWrapperSC>
          {isPrevControlVisible && (
            <HintsCarouselControlSC>
              <HintsArrowButton direction='prev' onClick={prev} />
            </HintsCarouselControlSC>
          )}
          <HintsScrollableRowSC ref={rowRef} onScroll={handleScroll}>
            {children}
          </HintsScrollableRowSC>
          {isNextControlVisible && (
            <HintsCarouselControlSC>
              <HintsArrowButton direction='next' onClick={next} />
            </HintsCarouselControlSC>
          )}
        </HintsCarouselWrapperSC>
      </HintsContainerSC>
    </HintsScrollContext.Provider>
  )
}
